import React from 'react'
import style from '../../pages/CensusManagement.module.scss'
import { DatePicker, Switch } from '@surecompanies/core_components'
import SaveEligibilityChangesButton from './SaveEligibilityEditsButton'
import dayjs from 'dayjs'
import { CensusFileMemberUpdatesSchema } from '../../api/dto/census-file-member-updates-schema.dto'
import { EligibilityData } from '../../types/eligibility-data'

interface ReviewEligibilityProps {
  employeeId: string
  onToggleChange: (id: string, checked: boolean) => void
  onDateChange: (id: string, field: string, date: Date | null) => void
  onDoNotCreateEnrollmentChange: (id: string, checked: boolean) => void
  eligibilityData: EligibilityData
  updateMemberUpdates: (
    employeeId: string,
    updates: Partial<CensusFileMemberUpdatesSchema>
  ) => void
  hasChanges: boolean
  setHasChanges: (hasChanges: boolean) => void
}

const ReviewEligibility: React.FC<ReviewEligibilityProps> = ({
  employeeId,
  onToggleChange,
  onDateChange,
  onDoNotCreateEnrollmentChange,
  eligibilityData,
  updateMemberUpdates,
  hasChanges,
  setHasChanges,
}) => {
  const doNotCreateEnrollment = eligibilityData.doNotCreateEnrollment

  const handleSave = () => {
    const { benefitsEffectiveDate, enrollmentStartDate, enrollmentEndDate } =
      eligibilityData

    // When "Do Not Create New Enrollment" is on, the proposed dates are
    // intentionally suppressed (never sent), so the all-or-none date
    // validation does not apply.
    if (!doNotCreateEnrollment) {
      const isAnyDateFilled =
        benefitsEffectiveDate !== null ||
        enrollmentStartDate !== null ||
        enrollmentEndDate !== null

      const areAllDatesFilled =
        benefitsEffectiveDate !== null &&
        enrollmentStartDate !== null &&
        enrollmentEndDate !== null

      if (isAnyDateFilled && !areAllDatesFilled) {
        alert(
          'Please fill out all the date fields for Review Eligibility before saving.'
        )
        return // Prevent save action
      }
    }
    updateMemberUpdates(employeeId, eligibilityData)
    setHasChanges(false)
  }

  const benefitsEffectiveDate = eligibilityData.benefitsEffectiveDate
    ? dayjs(eligibilityData.benefitsEffectiveDate)
    : null
  const enrollmentStartDate = eligibilityData.enrollmentStartDate
    ? dayjs(eligibilityData.enrollmentStartDate)
    : null
  const enrollmentEndDate = eligibilityData.enrollmentEndDate
    ? dayjs(eligibilityData.enrollmentEndDate)
    : null

  return (
    <div id="review-eligibility-container" className={style.container}>
      {/* Header Row */}
      <div className={style.headerRow}>
        <h3 className={style.title}>Review Eligibility</h3>
        <div className={style.toggleContainer}>
          <label>Benefits Eligible</label>
          <Switch
            data-eligibility-field="true"
            checked={eligibilityData.benefitsEligible}
            onChange={(checked) => {
              setHasChanges(true)
              onToggleChange(employeeId, checked)
            }}
          />
        </div>
      </div>

      {/* Do Not Create New Enrollment Row */}
      <div className={style.toggleContainer}>
        <label>Do Not Create New Enrollment</label>
        <Switch
          data-eligibility-field="true"
          checked={doNotCreateEnrollment}
          onChange={(checked) => {
            setHasChanges(true)
            onDoNotCreateEnrollmentChange(employeeId, checked)
          }}
        />
      </div>

      {/* Date Pickers Row — hidden when the enrollment window is being suppressed */}
      {!doNotCreateEnrollment && (
        <div className={style.dateRow}>
          <div className={style.dateItem}>
            <label>Benefits Effective Date</label>
            <DatePicker
              className={style.datePicker}
              data-eligibility-field="true"
              value={benefitsEffectiveDate}
              onChange={(date) => {
                setHasChanges(true)
                const formattedDate = dayjs(date).toDate()
                onDateChange(employeeId, 'benefitsEffectiveDate', formattedDate)
              }}
            />
          </div>
          <div className={style.dateItem}>
            <label>Special Enrollment Start Date</label>
            <DatePicker
              className={style.datePicker}
              data-eligibility-field="true"
              value={enrollmentStartDate}
              onChange={(date) => {
                setHasChanges(true)
                const formattedDate = dayjs(date).toDate()
                onDateChange(employeeId, 'enrollmentStartDate', formattedDate)
              }}
            />
          </div>
          <div className={style.dateItem}>
            <label>Special Enrollment End Date</label>
            <DatePicker
              className={style.datePicker}
              data-eligibility-field="true"
              value={enrollmentEndDate}
              onChange={(date) => {
                setHasChanges(true)
                const formattedDate = dayjs(date).toDate()
                onDateChange(employeeId, 'enrollmentEndDate', formattedDate)
              }}
            />
          </div>
        </div>
      )}

      {/* Save Button - Disabled if no changes */}
      <div className={style.buttonContainer}>
        <SaveEligibilityChangesButton
          employeeId={employeeId}
          eligibilityData={eligibilityData}
          updateMemberUpdates={updateMemberUpdates}
          disabled={!hasChanges} // Disable button if no changes are made
          onSave={handleSave} // Reset `hasChanges` after saving
        />
      </div>
    </div>
  )
}

export default ReviewEligibility
